import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import { ErrorBoundary } from "./components/CrashScreen";
import {
  isStaleChunkError,
  handleStaleChunk,
} from "./utils/staleChunkRecovery";
import "./index.css";

// Catch unhandled promise rejections (dynamic imports, failed fetches, etc.)
window.addEventListener("unhandledrejection", (event) => {
  const error = event.reason;

  if (isStaleChunkError(error)) {
    event.preventDefault();
    handleStaleChunk();
    return;
  }

  // Let React ErrorBoundary handle render errors — only log others
  console.error("[desktop] Unhandled rejection:", error);
});

// Catch synchronous errors not caught by React (e.g., module-level throws)
window.addEventListener("error", (event) => {
  if (isStaleChunkError(event.error ?? event.message)) {
    event.preventDefault();
    handleStaleChunk();
  }
});

// ---------------------------------------------------------------------------

// Clean up cache-busting query param after a successful reload
{
  const url = new URL(window.location.href);
  if (url.searchParams.has("_cb")) {
    url.searchParams.delete("_cb");
    window.history.replaceState(null, "", url.pathname + url.search + url.hash);
  }
}

// Stamp session-start as early as possible so "uptime" reflects time since
// the tab was loaded, not since a widget first mounted.
{
  const SESSION_START_KEY = "desktop-ui:session-started-at";
  try {
    if (!window.sessionStorage.getItem(SESSION_START_KEY)) {
      window.sessionStorage.setItem(SESSION_START_KEY, String(Date.now()));
    }
  } catch {
    // sessionStorage may be unavailable (private mode, etc.) — non-fatal
  }
}

// Preload the active wallpaper variant before React mounts. ThemeContext
// caches the resolved theme + Desktop caches the active wallpaper URL pair
// in localStorage — together that lets us start the wallpaper fetch in
// parallel with the JS bundle parse instead of waiting on the first React
// commit. First-time visitors fall through (no cached values) and pay the
// original render-time fetch.
{
  try {
    const themeRaw = localStorage.getItem("desktop-ui:theme-mode");
    const theme: "light" | "dark" =
      themeRaw === "system"
        ? window.matchMedia("(prefers-color-scheme: dark)").matches
          ? "dark"
          : "light"
        : themeRaw === "light"
          ? "light"
          : "dark";
    const scope = localStorage.getItem("desktop-ui:last-active-scope");
    const wpRaw = scope
      ? localStorage.getItem(`desktop-ui:active-wallpaper::${scope}`)
      : null;
    if (wpRaw) {
      const wp = JSON.parse(wpRaw) as { darkUrl?: string; lightUrl?: string };
      const url = theme === "dark" ? wp.darkUrl : wp.lightUrl;
      if (typeof url === "string" && url.startsWith("https://")) {
        const link = document.createElement("link");
        link.rel = "preload";
        link.as = "image";
        link.href = url;
        // Tell the browser this is high-priority — it's the LCP element.
        link.setAttribute("fetchpriority", "high");
        document.head.appendChild(link);
      }
    }
  } catch {
    // localStorage / parse failure — silently skip the optimization
  }
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);
